import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { Toaster } from '@/components/ui/sonner';
import { AuthPage } from '@/pages/AuthPage';
import { LandingPage } from '@/pages/LandingPage';
import { SegmentPage } from '@/pages/SegmentPage';
import { TermsPage } from '@/pages/TermsPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { ProtectedLayout } from '@/components/ProtectedLayout';

// Lazy-load the authenticated app so first-time visitors don't download it with the landing page
const Dashboard = React.lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const IngredientsPage = React.lazy(() => import('@/pages/IngredientsPage').then((m) => ({ default: m.IngredientsPage })));
const IngredientFormPage = React.lazy(() => import('@/pages/IngredientFormPage').then((m) => ({ default: m.IngredientFormPage })));
const RecipesPage = React.lazy(() => import('@/pages/RecipesPage').then((m) => ({ default: m.RecipesPage })));
const RecipeFormPage = React.lazy(() => import('@/pages/RecipeFormPage').then((m) => ({ default: m.RecipeFormPage })));
const RecipeCostingPage = React.lazy(() => import('@/pages/RecipeCostingPage').then((m) => ({ default: m.RecipeCostingPage })));
const SettingsPage = React.lazy(() => import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AnalyticsPage = React.lazy(() => import('@/pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const AdminPage = React.lazy(() => import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const HelpPage = React.lazy(() => import('@/pages/HelpPage').then((m) => ({ default: m.HelpPage })));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-[#6B7280]">Loading...</div>
  </div>
);

// Public home: landing page for logged-out visitors, dashboard for logged-in users
const PublicHome = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
};

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<PublicHome />} />
              <Route path="/for/:segment" element={<SegmentPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route element={<ProtectedLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/ingredients" element={<IngredientsPage />} />
                <Route path="/ingredients/new" element={<IngredientFormPage />} />
                <Route path="/ingredients/:id/edit" element={<IngredientFormPage />} />
                <Route path="/recipes" element={<RecipesPage />} />
                <Route path="/recipes/new" element={<RecipeFormPage />} />
                <Route path="/recipes/:id/edit" element={<RecipeFormPage />} />
                <Route path="/recipes/:id" element={<RecipeCostingPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/help" element={<HelpPage />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" />
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
